import React, { createContext, useContext, useState, useEffect } from 'react';
import useAuth from '@jetshop/core/components/AuthContext/useAuth';
import { useQuery } from 'react-apollo';
import customerIdQuery from '../components/Gamifiera/CustomerIdQuery.gql';
import storeLocatorQuery from '../components/StoreLocator/StoreLocator.gql';
import ChannelContext from '@jetshop/core/components/ChannelContext';

const FAVORITE_STORE_KEY = 'favorite_store';

// Channel to region mapping (same as StoreStock.js)
const countries = {
  'B2C-sv': 'Sverige',
  SV: 'Sverige',
  'B2C-dk': 'Danmark',
  DK: 'Danmark',
  'B2C-no': 'Norge',
  NO: 'Norge',
  'B2C-fi': 'Suomi',
  FI: 'Suomi',
  EU: 'Europa',
  NL: 'Nederland'
};

const FavoriteStoreContext = createContext();

export const useFavoriteStore = () => {
  const context = useContext(FavoriteStoreContext);
  if (!context) {
    throw new Error(
      'useFavoriteStore must be used within a FavoriteStoreProvider'
    );
  }
  return context;
};

export const FavoriteStoreProvider = ({ children }) => {
  const [favoriteStore, setFavoriteStoreState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { loggedIn } = useAuth();
  const { selectedChannel } = useContext(ChannelContext);

  // Get customer data for Voyado API
  const { data: customerData, loading: customerLoading } = useQuery(
    customerIdQuery,
    {
      skip: !loggedIn,
      errorPolicy: 'ignore',
      fetchPolicy: 'no-cache'
    }
  );

  // Get stores data for matching
  const { data: storesData, loading: storesLoading } =
    useQuery(storeLocatorQuery);

  // Helper function to get stores for current region (same as PDP)
  const getAllStores = () => {
    const allStores = storesData?.stores || [];
    const currentRegion = countries[selectedChannel?.name];

    if (!currentRegion) {
      return allStores;
    }

    return allStores.filter(store => store.region === currentRegion);
  };

  // Initialize from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(FAVORITE_STORE_KEY);
      if (stored) {
        setFavoriteStoreState(stored);
      }
    } catch (e) {
      console.error('Error accessing localStorage:', e);
    }
  }, []);

  // Fetch from Voyado and match with store data (SAME logic as PDP)
  useEffect(() => {
    const fetchAndMatchFavoriteStore = async () => {
      if (
        !loggedIn ||
        !customerData?.customer?.externalId ||
        customerLoading ||
        storesLoading
      ) {
        if (!loggedIn) {
          setFavoriteStoreState(null);
        }
        return;
      }

      // Check if we already have a valid favorite store in localStorage
      const availableStores = getAllStores();
      try {
        const storedFavoriteStore = localStorage.getItem(FAVORITE_STORE_KEY);
        if (storedFavoriteStore) {
          const storeExists = availableStores.find(
            store => store.name === storedFavoriteStore
          );
          if (storeExists) {
            setFavoriteStoreState(storedFavoriteStore);
            return; // We have a valid favorite store, no need to fetch from Voyado
          } else {
            localStorage.removeItem(FAVORITE_STORE_KEY);
          }
        }
      } catch (e) {
        console.error('Error accessing localStorage:', e);
      }

      setLoading(true);
      setError(null);

      try {
        const url = `/api/customer_favorite_store?externalId=${encodeURIComponent(
          customerData.customer.externalId
        )}`;

        const response = await fetch(url);
        const data = await response.json();

        if (response.ok && data.favoriteStore) {
          // Use SAME matching logic as StoreStock.js
          const voyadoFavoriteStore = data.favoriteStore;
          let voyadoStoreName = null;

          if (typeof voyadoFavoriteStore === 'string') {
            voyadoStoreName = voyadoFavoriteStore;
          } else if (voyadoFavoriteStore.name) {
            voyadoStoreName = voyadoFavoriteStore.name;
          } else if (voyadoFavoriteStore.favoriteStore?.name) {
            voyadoStoreName = voyadoFavoriteStore.favoriteStore.name;
          }

          if (voyadoStoreName) {
            // Find matching store (exact match or fuzzy match)
            let matchingStore = availableStores.find(
              store => store.name === voyadoStoreName
            );

            // If no exact match, try fuzzy matching
            if (!matchingStore) {
              matchingStore = availableStores.find(
                store =>
                  store.name
                    .toLowerCase()
                    .includes(voyadoStoreName.toLowerCase()) ||
                  voyadoStoreName
                    .toLowerCase()
                    .includes(store.name.toLowerCase())
              );
            }

            if (matchingStore) {
              setFavoriteStoreState(matchingStore.name);
              localStorage.setItem(FAVORITE_STORE_KEY, matchingStore.name);

              // Dispatch custom event for backward compatibility
              window.dispatchEvent(
                new CustomEvent('favoriteStoreChanged', {
                  detail: matchingStore.name
                })
              );
            }
          }
        } else {
          setError(data.error);
        }
      } catch (err) {
        console.error('Error fetching favorite store from Voyado:', err);
        setError('Failed to fetch favorite store');
      } finally {
        setLoading(false);
      }
    };

    fetchAndMatchFavoriteStore();
  }, [
    loggedIn,
    customerData?.customer?.externalId,
    customerLoading,
    storesLoading,
    selectedChannel?.name
  ]);

  const setFavoriteStore = storeName => {
    setFavoriteStoreState(storeName);

    try {
      if (storeName) {
        localStorage.setItem(FAVORITE_STORE_KEY, storeName);
      } else {
        localStorage.removeItem(FAVORITE_STORE_KEY);
      }

      // Dispatch custom event for backward compatibility
      window.dispatchEvent(
        new CustomEvent('favoriteStoreChanged', {
          detail: storeName
        })
      );
    } catch (e) {
      console.error('Error saving to localStorage:', e);
    }
  };

  const clearFavoriteStore = () => {
    setFavoriteStore(null);
  };

  // Stock checking for favorite store (same as before)
  const checkFavoriteStoreStock = product => {
    if (!favoriteStore || !product) {
      return null;
    }

    const hasVariants = product?.variants?.values?.length > 0;

    if (hasVariants) {
      const variants = product.variants.values;
      for (const variant of variants) {
        if (variant.warehouseStock?.length > 0) {
          const favoriteStoreStock = variant.warehouseStock.find(
            stock => stock.location && stock.location.name === favoriteStore
          );
          if (favoriteStoreStock && favoriteStoreStock.stockLevel > 0) {
            return true;
          }
        }
      }
      return false;
    } else {
      if (product.warehouseStock?.length > 0) {
        const favoriteStoreStock = product.warehouseStock.find(
          stock => stock.location && stock.location.name === favoriteStore
        );
        return favoriteStoreStock ? favoriteStoreStock.stockLevel > 0 : false;
      }
      return false;
    }
  };

  return (
    <FavoriteStoreContext.Provider
      value={{
        favoriteStore,
        setFavoriteStore,
        clearFavoriteStore,
        checkFavoriteStoreStock,
        loading,
        error,
        // Expose helper functions for components that need them
        getAllStores: () => getAllStores(),
        storesLoading
      }}
    >
      {children}
    </FavoriteStoreContext.Provider>
  );
};
